import { useState, useEffect, useLayoutEffect } from "react";
import styles from "./SplashScreen.module.css";

interface Props {
  name: string;
  tag?: string;
  /** Optional line rendered BELOW the name (e.g. "By Prodigium Dynamics"). */
  subtitle?: string;
  /** Use a solid black background instead of the accent blue. */
  dark?: boolean;
  onComplete?: () => void;
}

const isBrowser = typeof window !== "undefined";

function getSplashSeen(name: string): boolean {
  if (!isBrowser) return false;
  return sessionStorage.getItem(`splash_${name}`) === "1";
}

function markSplashSeen(name: string) {
  if (!isBrowser) return;
  sessionStorage.setItem(`splash_${name}`, "1");
}

export function SplashScreen({ name, tag, subtitle, dark, onComplete }: Props) {
  // Leer sessionStorage sincrónicamente para evitar flash de renderizado
  const [gone, setGone] = useState(() => getSplashSeen(name));
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    if (getSplashSeen(name)) {
      setGone(true);
      onComplete?.();
      return;
    }
    markSplashSeen(name);
    // Posponer un frame para que el navegador tenga tiempo de preparar
    // el compositor layer antes de iniciar la animación CSS
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimate(true));
    });
    const t = setTimeout(() => {
      setGone(true);
      onComplete?.();
    }, 1800);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [name, onComplete]);

  if (gone) return null;

  return (
    <div className={`${styles.splash} ${dark ? styles.splashDark : ""} ${animate ? styles.splashActive : ""}`}>
      {tag ? <p className={styles.tag}>{tag}</p> : null}
      <h1 className={styles.name}>{name}</h1>
      {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
      <span className={styles.line} />
      <div className={styles.ringWrap}>
        <svg className={styles.ringSvg} width="32" height="32" viewBox="0 0 32 32">
          <circle className={styles.ringTrack} cx="16" cy="16" r="14" />
          <circle className={styles.ringBar} cx="16" cy="16" r="14" />
        </svg>
      </div>
    </div>
  );
}
